import { useCallback, useEffect, useState } from 'react';
import { fitLinkagePolynomialFromCsvText } from '../utils/jackLinkageCsv';
import {
  loadUserLinkagePresets,
  newJackBikeProfileId,
  normalizeJackBikeLabel,
  saveUserLinkagePresets,
} from '../utils/jackSettings';

/**
 * Shared linkage dropdown, CSV import, and saved-preset CRUD for Settings and bike onboarding.
 * Expects `main` / `commitMain` from the same Jack main settings state as the rest of the app.
 */
export function useJackLinkageHandlers({
  main,
  commitMain,
  communityPresets,
  userLinkagePresets,
  setUserLinkagePresets,
  /** Optional: e.g. admin publishes curve to Firestore `publicLinkagePresets`. */
  afterSaveUserLinkagePreset,
}) {
  const [linkageStatus, setLinkageStatus] = useState('');
  const [linkagePresetNameInput, setLinkagePresetNameInput] = useState('');

  useEffect(() => {
    const src = main.linkageSource;
    if (typeof src === 'string' && src.startsWith('saved:')) {
      const id = src.slice(6);
      const p = userLinkagePresets.find((x) => x.id === id);
      setLinkagePresetNameInput(p ? String(p.name || '') : '');
      return;
    }
    if (src === 'custom') return;
    setLinkagePresetNameInput('');
  }, [main.linkageSource, userLinkagePresets]);

  const updateMain = useCallback(
    (patch) => {
      commitMain((prev) => ({ ...prev, ...patch }));
    },
    [commitMain],
  );

  const applyUserLinkagePreset = useCallback(
    (preset) => {
      if (!preset) return;
      commitMain((prev) => ({
        ...prev,
        linkageSource: `saved:${preset.id}`,
        rearA: preset.rearA,
        rearB: preset.rearB,
        rearC: preset.rearC,
        rearD: preset.rearD,
      }));
      setLinkageStatus('');
    },
    [commitMain],
  );

  const applyCommunityDoc = useCallback(
    (docRow) => {
      if (!docRow) return;
      const ra = Number(docRow.rearA);
      const rb = Number(docRow.rearB);
      const rc = Number(docRow.rearC);
      const rd = Number(docRow.rearD);
      if (![ra, rb, rc, rd].every((n) => Number.isFinite(n))) {
        setLinkageStatus('Invalid community preset data.');
        return;
      }
      commitMain((prev) => {
        const next = {
          ...prev,
          linkageSource: `community:${docRow.id}`,
          rearA: ra,
          rearB: rb,
          rearC: rc,
          rearD: rd,
        };
        if (Number.isFinite(Number(docRow.rearTravelMm))) {
          next.rearTravel = Math.round(Number(docRow.rearTravelMm));
        }
        if (Number.isFinite(Number(docRow.frontTravelMm))) {
          next.frontTravel = Math.round(Number(docRow.frontTravelMm));
        }
        return next;
      });
      setLinkageStatus('');
    },
    [commitMain],
  );

  const setCustomLinkage = useCallback(() => {
    commitMain((prev) => ({ ...prev, linkageSource: 'custom' }));
  }, [commitMain]);

  const onLinkageSelect = useCallback(
    (e) => {
      const v = e.target.value;
      if (v.startsWith('saved:')) {
        const id = v.slice(6);
        const preset = userLinkagePresets.find((x) => x.id === id);
        if (preset) applyUserLinkagePreset(preset);
        return;
      }
      if (v.startsWith('community:')) {
        const id = v.slice('community:'.length);
        const row = communityPresets.find((p) => p.id === id);
        if (row) applyCommunityDoc(row);
        return;
      }
      if (v === 'custom') {
        setCustomLinkage();
      }
    },
    [applyUserLinkagePreset, applyCommunityDoc, communityPresets, setCustomLinkage, userLinkagePresets],
  );

  /**
   * @param {Event} event
   * @param {{ keepLinkageSource?: boolean }} [opts] If true, do not switch to `custom` (e.g. editing a saved preset).
   */
  const handleLinkageCsv = useCallback(
    async (event, opts = {}) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const result = fitLinkagePolynomialFromCsvText(text);
        if (!result.ok) {
          setLinkageStatus(result.error || 'Invalid CSV.');
          return;
        }
        const patch = {
          ...result.coefficients,
          rearTravel: result.rearTravel,
        };
        if (!opts.keepLinkageSource) {
          patch.linkageSource = 'custom';
        }
        updateMain(patch);
        setLinkageStatus(`Loaded ${result.points.length} points. Rear travel set to ${result.rearTravel} mm.`);
      } catch (_err) {
        setLinkageStatus('Failed to load linkage CSV.');
      } finally {
        event.target.value = '';
      }
    },
    [updateMain],
  );

  const handleSaveNewLinkagePreset = useCallback(async () => {
    const nameNorm = normalizeJackBikeLabel(linkagePresetNameInput);
    if (!nameNorm) {
      alert('Enter a name for this linkage preset.');
      return;
    }
    const existing = loadUserLinkagePresets();
    if (existing.some((p) => normalizeJackBikeLabel(p.name) === nameNorm)) {
      alert('A linkage preset with this name already exists (names are not case-sensitive).');
      return;
    }
    const list = [...existing];
    const id = newJackBikeProfileId();
    const newPreset = {
      id,
      name: nameNorm,
      rearA: main.rearA,
      rearB: main.rearB,
      rearC: main.rearC,
      rearD: main.rearD,
    };
    list.push(newPreset);
    saveUserLinkagePresets(list);
    setUserLinkagePresets(list);
    commitMain((prev) => ({ ...prev, linkageSource: `saved:${id}` }));
    setLinkagePresetNameInput('');
    let extra = '';
    if (typeof afterSaveUserLinkagePreset === 'function') {
      try {
        const r = await afterSaveUserLinkagePreset({
          id,
          nameNorm,
          rearA: main.rearA,
          rearB: main.rearB,
          rearC: main.rearC,
          rearD: main.rearD,
        });
        if (typeof r === 'string' && r) extra = ` ${r}`;
      } catch (e) {
        console.warn('afterSaveUserLinkagePreset:', e);
        extra = ' (public publish failed — check console.)';
      }
    }
    setLinkageStatus(`Saved linkage preset “${nameNorm}”.${extra}`);
  }, [
    main.rearA,
    main.rearB,
    main.rearC,
    main.rearD,
    linkagePresetNameInput,
    commitMain,
    setUserLinkagePresets,
    afterSaveUserLinkagePreset,
  ]);

  const deleteCurrentLinkagePreset = useCallback(() => {
    const src = main.linkageSource;
    if (!src || !String(src).startsWith('saved:')) return;
    const id = src.slice(6);
    if (!window.confirm('Delete this linkage preset?')) return;
    const list = loadUserLinkagePresets().filter((p) => p.id !== id);
    saveUserLinkagePresets(list);
    setUserLinkagePresets(list);
    setLinkagePresetNameInput('');
    commitMain((prev) => ({ ...prev, linkageSource: 'custom' }));
    setLinkageStatus('Linkage preset deleted.');
  }, [main.linkageSource, commitMain, setUserLinkagePresets]);

  /** Overwrite the selected saved preset (name + a–d) in local storage. @returns {boolean} */
  const handleUpdateSavedLinkagePreset = useCallback(() => {
    const src = main.linkageSource;
    if (!src || !String(src).startsWith('saved:')) return false;
    const id = src.slice(6);
    const nameNorm = normalizeJackBikeLabel(linkagePresetNameInput);
    if (!nameNorm) {
      alert('Enter a name for this linkage preset.');
      return false;
    }
    const existing = loadUserLinkagePresets();
    const idx = existing.findIndex((p) => p.id === id);
    if (idx < 0) {
      setLinkageStatus('Selected preset not found.');
      return false;
    }
    const nameTaken = existing.some(
      (p) => p.id !== id && normalizeJackBikeLabel(p.name) === nameNorm,
    );
    if (nameTaken) {
      alert('Another linkage preset already uses this name.');
      return false;
    }
    const next = [...existing];
    next[idx] = {
      ...next[idx],
      name: nameNorm,
      rearA: Number(main.rearA),
      rearB: Number(main.rearB),
      rearC: Number(main.rearC),
      rearD: Number(main.rearD),
    };
    saveUserLinkagePresets(next);
    setUserLinkagePresets(next);
    setLinkageStatus(`Updated linkage preset “${nameNorm}”.`);
    return true;
  }, [
    main.linkageSource,
    main.rearA,
    main.rearB,
    main.rearC,
    main.rearD,
    linkagePresetNameInput,
    setUserLinkagePresets,
  ]);

  const linkageSelectValue = main.linkageSource || 'custom';
  const linkageSavedSelected =
    typeof main.linkageSource === 'string' && main.linkageSource.startsWith('saved:');

  return {
    linkageStatus,
    setLinkageStatus,
    linkagePresetNameInput,
    setLinkagePresetNameInput,
    updateMain,
    onLinkageSelect,
    handleLinkageCsv,
    handleSaveNewLinkagePreset,
    handleUpdateSavedLinkagePreset,
    deleteCurrentLinkagePreset,
    linkageSelectValue,
    linkageSavedSelected,
  };
}
